import { TAX_PERCENTAGE } from "../../../util/constants.js"

export function makePackageNode(pkg, options = {}) {
	const rowId = options.rowId ?? pkg.rowId ?? pkg.id ?? pkg._id ?? `pkg_${Date.now()}_${Math.random().toString(36).slice(2)}`
	return {
		rowId,
		id: pkg.id ?? pkg._id,
		name: pkg.name ?? '',
		quantity: Number(pkg.quantity) ?? 1,
		unitPrice: parseFloat(pkg.unitPrice ?? pkg.price) || 0,
		type: 'package',
		image: pkg.image || "",
		addOns: Array.isArray(pkg.addOns) ? pkg.addOns.map((a, i) => makeAddOnNode(a, { parentPackageId: rowId, index: i })) : [],
	}
}

export function makeAddOnNode(addon, options = {}) {
	const parentId = options.parentPackageId ?? ''
	const id = addon.id ?? addon._id ?? `custom_${Date.now()}_${Math.random().toString(36).slice(2)}`
	const rowId = options.rowId ?? addon.rowId ?? `addon_${parentId}_${id}_${(options.index ?? 0)}`
	return {
		rowId,
		id,
		name: addon.name ?? '',
		quantity: Number(addon.quantity) ?? 1,
		unitPrice: parseFloat(addon.unitPrice ?? addon.price) || 0,
		type: addon.type ?? 'addOn',
		image: addon.image || "",
		minQuantity: addon.minQuantity || 0,
		maxQuantity: addon.maxQuantity || 0,
	}
}

export function formatPackageData(pkg) {
	if(!pkg || !Object.keys(pkg).length) return {
		packages: [],
		total: null,
		taxPercentage: null,
		tax: null,
		grandTotal: null,
	}
	const packageNode = makePackageNode({
		id: pkg._id,
		name: pkg.name,
		quantity: pkg.quantity,
		unitPrice: pkg.price,
		image: pkg.image || "",
		addOns: [],
	})
	if (pkg.addOnsIds?.length > 0) {
		packageNode.addOns = pkg.addOnsIds.map((addon, i) =>
			makeAddOnNode(
				{
					_id: addon._id,
					name: addon.name,
					quantity: addon.quantity,
					price: addon?.price,
					type: 'addOn',
					image: addon.image || "",
					minQuantity: addon.minQuantity || 0,
					maxQuantity: addon.maxQuantity || 0,
				},
				{ parentPackageId: packageNode.rowId, index: i }
			)
		)
	}
	const packages = [packageNode]
	const total = totalFromTree(packages)
	const taxPercentage = TAX_PERCENTAGE
	const tax = total * (taxPercentage / 100)
	const grandTotal = total + tax
	return {
		packages,
		total,
		taxPercentage,
		tax,
		grandTotal,
	}
}

export function flattenTree(packages) {
	const list = []
	;(packages || []).forEach((pkg) => {
		list.push({ ...pkg, isPackage: true })
		;(pkg.addOns || []).forEach((addon) => list.push({ ...addon, isPackage: false }))
	})
	return list
}

export function totalFromTree(packages) {
	return flattenTree(packages).reduce(
		(sum, item) => sum + (Number(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0),
		0
	)
}

export function normalizeToTree(data) {
	if (Array.isArray(data?.packages) && data.packages.length > 0) {
		return data.packages.map((p) => {
			const node = makePackageNode(p)
			return {
				...node,
				addOns: (p.addOns || []).map((a, i) => makeAddOnNode(a, { parentPackageId: node.rowId, index: i })),
			}
		})
	}
	const flat = Array.isArray(data?.lineItems) ? data.lineItems : []
	const tree = []
	let currentPackage = null
	flat.forEach((item) => {
		const normalized = {
			id: item.id ?? item._id,
			name: item.name ?? '',
			quantity: Number(item.quantity) || 0,
			unitPrice: parseFloat(item.unitPrice) || 0,
			type: item.type ?? 'custom',
			image: item.image || "",
			minQuantity: item.minQuantity || 0,
			maxQuantity: item.maxQuantity || 0,
		}
		if (normalized.type === 'package') {
			currentPackage = makePackageNode({ ...normalized, addOns: [] })
			tree.push(currentPackage)
		} else if (currentPackage && (normalized.type === 'addOn' || normalized.type === 'custom')) {
			currentPackage.addOns.push(makeAddOnNode(normalized, { parentPackageId: currentPackage.rowId, index: currentPackage.addOns.length }))
		} else {
			currentPackage = makePackageNode({ ...normalized, type: 'package', addOns: [] })
			tree.push(currentPackage)
		}
	})
	return tree
}

export function treeToPayload(packages) {
	return (packages || []).map((pkg) => ({
		_id: pkg.id,
		name: pkg.name,
		quantity: pkg.quantity,
		price: pkg.unitPrice,
		image: pkg.image || "",
		addOns: (pkg.addOns || []).map((a) => ({
			_id: a.id,
			name: a.name,
			quantity: a.quantity,
			price: a.unitPrice,
			image: a.image || "",
			minQuantity: a.minQuantity || 0,
			maxQuantity: a.maxQuantity || 0,
		})),
	}))
}
